import React, { useState } from 'react';
import axios from 'axios';

const TestMagicLinks = () => {
  const [email, setEmail] = useState('');
  const [redirectTo, setRedirectTo] = useState('/shortlists');
  const [generatedLink, setGeneratedLink] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const testScenarios = [
    { name: 'Shortlists Page', path: '/shortlists' },
    { name: 'Browse Candidates', path: '/browse-candidates' },
    { name: 'Applications', path: '/applications' },
    { name: 'Specific Role', path: '/roles/rec123456' },
    { name: 'Interview Requests', path: '/interviews/requests' }
  ];

  const generateMagicLink = async () => {
    if (!email) {
      setError('Email is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post('/api/auth/generate-link', {
        email,
        redirectTo,
        purpose: 'automated_email'
      });

      setGeneratedLink(response.data.magicLink);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to generate magic link');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedLink);
    alert('Link copied to clipboard!');
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">Magic Link Testing</h1>
          
          <div className="space-y-6">
            {/* Email Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Test Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="user@company.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Redirect Path */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Redirect Path
              </label>
              <input
                type="text"
                value={redirectTo}
                onChange={(e) => setRedirectTo(e.target.value)}
                placeholder="/shortlists"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Quick Scenarios */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quick Test Scenarios
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {testScenarios.map((scenario) => (
                  <button
                    key={scenario.path}
                    onClick={() => setRedirectTo(scenario.path)}
                    className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-md text-gray-700 transition-colors"
                  >
                    {scenario.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Generate Button */}
            <button
              onClick={generateMagicLink}
              disabled={loading || !email}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Generating...' : 'Generate Magic Link'}
            </button>

            {/* Error Display */}
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                <p className="text-red-800">{error}</p>
              </div>
            )}

            {/* Generated Link */}
            {generatedLink && (
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">
                  Generated Magic Link
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={generatedLink}
                    readOnly
                    className="flex-1 px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-sm"
                  />
                  <button
                    onClick={copyToClipboard}
                    className="px-3 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 text-sm"
                  >
                    Copy
                  </button>
                </div>
                <div className="flex space-x-2">
                  <a
                    href={generatedLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm"
                  >
                    Test Link
                  </a>
                  <button
                    onClick={() => setGeneratedLink('')}
                    className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 text-sm"
                  >
                    Clear
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Instructions */}
          <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-md">
            <h3 className="font-medium text-blue-900 mb-2">Testing Instructions</h3>
            <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
              <li>Enter a test email address (use a real email you can access)</li>
              <li>Select or enter the redirect path where users should land</li>
              <li>Click "Generate Magic Link" to create the link</li>
              <li>Copy the link and test it in a new incognito window</li>
              <li>Verify that after login, you're redirected to the correct page</li>
            </ol>
          </div>

          {/* Migration Guide */}
          <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
            <h3 className="font-medium text-yellow-900 mb-2">Migration from Old URLs</h3>
            <div className="text-sm text-yellow-800 space-y-2">
              <p><strong>Old format:</strong></p>
              <code className="block bg-yellow-100 p-2 rounded text-xs">
                https://app.jumpstart-uk.com/?username=Jumpstart1&password=Jumpstart1411558&page=/shortlists
              </code>
              <p><strong>New format:</strong></p>
              <code className="block bg-yellow-100 p-2 rounded text-xs">
                https://app.jumpstart-uk.com/auth/verify?token=abc123...&redirectTo=%2Fshortlists
              </code>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestMagicLinks; 